import { useEffect, useMemo, useRef, useState } from 'react';
import { Signal } from '../../../../types/signals-live';
import {
  getPaginatedSignals,
  getSignalById,
  reclassifySignal,
  bulkReclassifySignals,
  deleteSignal,
  deleteSignalsBulk,
} from '../../../../utils/api';
import { useRealtimeSubscriptions, useOrganizationUpdates } from '../../../../hooks/useRealtimeUpdates';
import type { FiltersModalValues } from '../../signals-live/FiltersModal';
import {
  applySignalRealtimeMessage,
  getRealtimeClassificationSignalId,
} from './conductFeedRealtime';

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export type QuickFilter = { key: string; name: string; dimension: string };

export interface UseConductFeedOptions {
  filterValues: FiltersModalValues;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams, opts?: { replace?: boolean }) => void;
  paginationLimit?: number;
  showNotice: (notice: { type: 'success' | 'error' | 'info'; message: string; autoDismissMs?: number; details?: any }) => void;
}

export function useConductFeed(options: UseConductFeedOptions) {
  const {
    filterValues,
    searchParams,
    setSearchParams,
    paginationLimit = 20,
    showNotice,
  } = options;

  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [threadOpen, setThreadOpen] = useState<Record<string, boolean>>({});
  const [detailCache, setDetailCache] = useState<Record<string, any>>({});

  const [sortBy, setSortBy] = useState<'severity_score' | 'received_at' | 'customer_name' | 'created_at' | 'complaint_detected' | 'vulnerability_detected'>('severity_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [pagination, setPagination] = useState<PaginationState>({ page: 1, limit: paginationLimit, total: 0, hasNext: false, hasPrev: false });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const [progressTick, setProgressTick] = useState(0);
  const [justUpdated, setJustUpdated] = useState<Record<string, boolean>>({});

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [quickFilters, setQuickFilters] = useState<QuickFilter[]>([]);
  const [datasetIds, setDatasetIds] = useState<string[]>([]);

  // Removed fallback timers; rely solely on backend websocket events

  // Read initial state from URL (search + quick filters + datasets)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    setSearchTerm(urlSearch);

    // Support comma-separated dataset IDs in URL
    const urlDatasetIdsParam = searchParams.get('datasetIds') || '';
    const urlDatasetIds = urlDatasetIdsParam.split(',').filter(Boolean);
    setDatasetIds(urlDatasetIds);

    const tax = (searchParams.get('taxonomyCategoryKeys') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (tax.length) {
      const items: QuickFilter[] = [];
      tax.forEach((k) => items.push({ key: k, name: (k.split(':')[1] || k).replace(/_/g, ' '), dimension: (k.split(':')[0] || '') }));
      setQuickFilters(items);
    } else {
      setQuickFilters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search sync + fetch
  const prevSearchTermRef = useRef<string>(searchTerm);
  useEffect(() => {
    const term = (searchTerm || '').trim();
    const searchTermChanged = prevSearchTermRef.current !== searchTerm;
    prevSearchTermRef.current = searchTerm;

    const t = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (term.length > 0) params.set('search', term);
      else params.delete('search');
      setSearchParams(params, { replace: true });
      if (searchTermChanged && (term.length === 0 || term.length >= 2)) {
        fetchSignals(1);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Periodic tick for progress labels
  useEffect(() => {
    const t = setInterval(() => setProgressTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Real-time updates via unified client with guaranteed delivery
  const { subscribe: subscribeRealtime } = useRealtimeSubscriptions();

  // Subscribe to organization-wide updates to catch new signals and broad updates
  const orgId = useMemo(() => {
    // Extract organization ID from signals if available
    return signals.length > 0 ? signals[0].organization_id : null;
  }, [signals]);

  const { messages: orgMessages } = useOrganizationUpdates(
    orgId || '',
    ['signal.created', 'signal.updated', 'signal.classified', 'signal.progress']
  );

  // No longer using polling fallback - relying on robust WebSocket real-time system

  // Handle real-time messages from organization-wide updates
  useEffect(() => {
    if (!orgMessages.length) return;

    const latestMessage = orgMessages[orgMessages.length - 1];
    const now = Date.now();
    const classifiedSignalId = getRealtimeClassificationSignalId(latestMessage);

    setDetailCache((prev) =>
      applySignalRealtimeMessage(
        { detailCache: prev, signals: [] },
        latestMessage,
        { now }
      ).detailCache
    );

    setSignals((prev) =>
      applySignalRealtimeMessage(
        { detailCache: {}, signals: prev },
        latestMessage,
        { now }
      ).signals
    );

    if (classifiedSignalId) {
      notifySignalClassified(classifiedSignalId);
    }
  }, [orgMessages]);


  // Subscribe to per-signal channels when expanded or pending
  useEffect(() => {
    const expandedIds = Object.keys(expanded).filter((id) => expanded[id]);
    // Extract pending IDs from signals without adding it as a dependency
    // This prevents re-subscriptions when signals array updates
    const pendingIds = signals.filter((s) => s.processing_status === 'pending').map((s) => s.id);
    const ids = Array.from(new Set([...expandedIds, ...pendingIds]));

    const unsubscribeFunctions: Array<() => void> = [];

    ids.forEach((id) => {
      const unsubscribe = subscribeRealtime('signal', id, (message) => {
        const now = Date.now();
        const classifiedSignalId = getRealtimeClassificationSignalId(message, id);

        setDetailCache((prev) =>
          applySignalRealtimeMessage(
            { detailCache: prev, signals: [] },
            message,
            { now, fallbackSignalId: id }
          ).detailCache
        );

        setSignals((prev) =>
          applySignalRealtimeMessage(
            { detailCache: {}, signals: prev },
            message,
            { now, fallbackSignalId: id }
          ).signals
        );

        if (classifiedSignalId) {
          notifySignalClassified(classifiedSignalId);
        }
      });
      unsubscribeFunctions.push(unsubscribe);
    });

    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, subscribeRealtime]);

  const notifySignalClassified = (signalId: string) => {
    setJustUpdated((prev) => ({ ...prev, [signalId]: true }));
    setTimeout(() => {
      setJustUpdated((prev) => {
        const next = { ...prev };
        delete next[signalId];
        return next;
      });
    }, 2000);

    showNotice({
      type: 'success',
      message: `Signal reclassified successfully`,
      autoDismissMs: 5000
    });
  };

  const fetchSignals = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const uiFilters: Record<string, any> = {};
      uiFilters.severity_min = filterValues.severityMin;
      uiFilters.severity_max = 100;

      if (filterValues.timeRange === '24h') {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        uiFilters.date_from = yesterday.toISOString();
        uiFilters.date_to = now.toISOString();
      } else if (filterValues.timeRange === '7d') {
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        uiFilters.date_from = weekAgo.toISOString();
        uiFilters.date_to = now.toISOString();
      } else if (filterValues.timeRange === 'custom' && filterValues.from && filterValues.to) {
        const fromDate = new Date(filterValues.from + 'T00:00:00');
        const toDate = new Date(filterValues.to + 'T23:59:59');
        uiFilters.date_from = fromDate.toISOString();
        uiFilters.date_to = toDate.toISOString();
      }

      const term = (searchTerm || '').trim();
      if (term.length > 0) uiFilters.search = term;

      const taxKeys = quickFilters.map((f) => f.key);
      if (taxKeys.length) uiFilters.taxonomy_category_keys = taxKeys;

      if (datasetIds.length > 0) {
        uiFilters.dataset_id = datasetIds.length === 1 ? datasetIds[0] : datasetIds;
      }

      const response: any = await getPaginatedSignals(page, pagination.limit, {
        sort_by: sortBy,
        sort_order: sortOrder,
        ...uiFilters,
      } as any);
      const items = response.items || response.signals || response.data || [];
      setSignals(items);

      setSelected((prev) => {
        const next = new Set<string>();
        items.forEach((it: any) => {
          if (prev.has(it.id)) next.add(it.id);
        });
        return next;
      });

      setPagination({
        page: response.page || page,
        limit: response.limit || pagination.limit,
        total: response.total !== undefined ? response.total : items.length,
        hasNext: response.has_next ?? page * (response.limit || pagination.limit) < (response.total || items.length),
        hasPrev: response.has_prev ?? page > 1,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  };

  const fetchEffectTimer = useRef<number | null>(null);
  useEffect(() => {
    if (fetchEffectTimer.current) {
      window.clearTimeout(fetchEffectTimer.current);
    }
    fetchEffectTimer.current = window.setTimeout(() => {
      fetchSignals(1);
    }, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, filterValues, datasetIds]);

  const toggleExpand = async (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!detailCache[id]) {
      try {
        const detail = await getSignalById(id);
        // Check if this signal is currently being reclassified
        const signal = signals.find(s => s.id === id);
        const isReclassifying = signal?.processing_status === 'pending';

        setDetailCache((prev) => {
          const merged = { ...detail } as any;
          // Preserve the pending status if actively reclassifying
          if (isReclassifying) {
            merged.processing_status = 'pending';
          }
          return { ...prev, [id]: merged };
        });
      } catch {}
    }
  };

  const toggleThread = async (id: string) => {
    setThreadOpen((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!detailCache[id]) {
      try {
        const detail = await getSignalById(id);
        setDetailCache((prev) => ({ ...prev, [id]: detail }));
      } catch {}
    }
  };

  const ensureDetail = async (id: string) => {
    if (!detailCache[id]) {
      try {
        const detail = await getSignalById(id);
        setDetailCache((prev) => ({ ...prev, [id]: detail }));
      } catch {}
    }
  };

  const handleReclassifySignal = async (signalId: string) => {
    const startedAt = Date.now();
    try {
      const { status } = await reclassifySignal(signalId);

      // No run started, so the writes below would stamp `reclassifying at 10 %`
      // over one already publishing its own progress - the feed showing progress
      // going backwards. Announced here because the menu holding the control
      // closes on the selecting click.
      if (status === 'already_classifying' || status === 'import_pending') {
        showNotice({
          type: 'info',
          message:
            status === 'already_classifying'
              ? 'This signal is already being classified.'
              : 'This signal is waiting for its import to finish.',
          autoDismissMs: 5000,
        });
        // Returned, not just shown: the menu this handler is also passed to
        // keeps its own success tick and would otherwise claim a refused run
        // started to anyone reopening it within three seconds.
        return status;
      }

      setDetailCache((prev) => ({
        ...prev,
        [signalId]: {
          ...(prev[signalId] || {}),
          processing_status: 'pending',
          _progress: {
            stage: 'reclassifying',
            percent: 10,
            ts: startedAt,
            startedAt,
            lastRealtimeAt: null,
          },
        },
      }));
      setSignals((prev) => prev.map((s) => (s.id === signalId ? { ...s, processing_status: 'pending' } : s)));
    } catch (error) {
      console.error('Failed to reclassify signal:', error);
      throw error;
    }
  };

  const handleBulkReclassify = async () => {
    if (selected.size === 0) {
      showNotice({
        type: 'error',
        message: 'No signals selected for reclassification'
      });
      return;
    }

    const signalIds = Array.from(selected);
    const confirmMessage = selected.size === 1
      ? 'Reclassify the selected signal?'
      : `Reclassify ${selected.size} selected signals?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const startedAt = Date.now();

      // Call bulk reclassify endpoint
      const response = await bulkReclassifySignals(signalIds);

      // Only the ids the response counts as started get an optimistic write.
      // The other three buckets were not enqueued, so no completion event is
      // coming for them and a pending stamp would never clear - refusals for
      // the reason above, and a failure because it never reached the queue.
      const notStarted = new Set([
        ...(response.already_classifying ?? []),
        ...(response.import_pending ?? []),
        ...(response.failed ?? []).map((f) => f.signal_id),
      ]);
      const startedIds = signalIds.filter((id) => !notStarted.has(id));

      // Update UI state for each signal
      startedIds.forEach(signalId => {
        setDetailCache((prev) => ({
          ...prev,
          [signalId]: {
            ...(prev[signalId] || {}),
            processing_status: 'pending',
            _progress: {
              stage: 'reclassifying',
              percent: 10,
              ts: startedAt,
              startedAt,
              lastRealtimeAt: null,
            },
          },
        }));
      });

      setSignals((prev) =>
        prev.map((s) =>
          startedIds.includes(s.id)
            ? { ...s, processing_status: 'pending' }
            : s
        )
      );

      // Clear selection after initiating reclassification
      setSelected(new Set());

      // Counted off the response, never off the request: a wholly refused
      // selection has `started: 0` and must report it. `?? 0` defaults a field an
      // older deployment may omit; it does not invent a count from the request.
      const startedCount = response.started;
      const alreadyCount = response.already_classifying?.length ?? 0;
      const importCount = response.import_pending?.length ?? 0;
      const failedCount = response.failed?.length ?? 0;

      // One sentence per outcome that happened. The added clauses are bare
      // counts because the opening sentence has already named the noun.
      let message = `Started reclassification for ${startedCount} signal${startedCount !== 1 ? 's' : ''}.`;
      if (alreadyCount > 0) message += ` ${alreadyCount} already classifying.`;
      if (importCount > 0) message += ` ${importCount} waiting for import.`;
      if (failedCount > 0) message += ` ${failedCount} signal${failedCount !== 1 ? 's' : ''} failed.`;

      showNotice({
        type: alreadyCount + importCount + failedCount === 0 ? 'success' : 'info',
        message,
        autoDismissMs: 5000
      });
    } catch (error) {
      console.error('Failed to bulk reclassify signals:', error);
      showNotice({
        type: 'error',
        message: 'Failed to start bulk reclassification. Please try again.',
        autoDismissMs: 5000
      });
    }
  };

  const allVisibleSelected = useMemo(() => signals.length > 0 && signals.every((s) => selected.has(s.id)), [signals, selected]);

  const toggleSelect = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) signals.forEach((s) => next.add(s.id));
      else signals.forEach((s) => next.delete(s.id));
      return next;
    });
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected signal${selected.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      const ids = Array.from(selected);
      let deletedCount = 0;
      let failedIds: string[] = [];
      try {
        const res: any = await deleteSignalsBulk(ids);
        deletedCount = Number(res?.deleted ?? 0);
        if (deletedCount < ids.length) {
          for (const id of ids) {
            try {
              await deleteSignal(id);
            } catch (err: any) {
              failedIds.push(id);
            }
          }
        }
      } catch (e) {
        for (const id of ids) {
          try {
            await deleteSignal(id);
            deletedCount += 1;
          } catch (err) {
            failedIds.push(id);
          }
        }
      }
      setSelected(new Set());
      await fetchSignals(1);
      if (failedIds.length === 0) {
        showNotice({ type: 'success', message: `Deleted ${ids.length} signal${ids.length > 1 ? 's' : ''}.`, autoDismissMs: 2500 });
      } else {
        showNotice({ type: 'error', message: `Deleted ${ids.length - failedIds.length} of ${ids.length} signals. Some could not be deleted.`, details: failedIds.slice(0, 10) });
      }
    } catch (e) {
      console.error('Failed to delete selected signals', e);
      showNotice({ type: 'error', message: 'Failed to delete selected signals. Please try again.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (field: 'severity_score' | 'received_at' | 'customer_name' | 'created_at' | 'complaint_detected' | 'vulnerability_detected') => {
    if (sortBy === field) setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleLabelTap = (meta: any) => {
    const dim: string | undefined = meta?.dimension_id;
    const catId: string | undefined = meta?.category_id;
    const name: string = meta?.category_name || meta?.label_name || 'Category';
    if (!dim || !catId) return;
    const key = `${dim}:${catId}`;
    setQuickFilters((prev) => {
      if (prev.some((f) => f.key === key)) return prev;
      const next = [...prev, { key, name, dimension: dim }];
      const params = new URLSearchParams(searchParams);
      const taxKeys = next.map((f) => f.key);
      if (taxKeys.length) params.set('taxonomyCategoryKeys', taxKeys.join(','));
      else params.delete('taxonomyCategoryKeys');
      setSearchParams(params, { replace: true });
      fetchSignals(1);
      return next;
    });
  };

  const removeQuickFilter = (key: string) => {
    setQuickFilters((prev) => {
      const next = prev.filter((f) => f.key !== key);
      const params = new URLSearchParams(searchParams);
      const taxKeys = next.map((f) => f.key);
      if (taxKeys.length) params.set('taxonomyCategoryKeys', taxKeys.join(','));
      else params.delete('taxonomyCategoryKeys');
      setSearchParams(params, { replace: true });
      fetchSignals(1);
      return next;
    });
  };

  const clearQuickFilters = () => {
    setQuickFilters([]);
    const params = new URLSearchParams(searchParams);
    params.delete('taxonomyCategoryKeys');
    setSearchParams(params, { replace: true });
    fetchSignals(1);
  };

  const debugExpandUrl = useMemo(() => {
    const taxKeys = quickFilters.map((f) => f.key);
    const params = new URLSearchParams();
    if (taxKeys.length) params.set('taxonomyCategoryKeys', taxKeys.join(','));
    return `/v3/taxonomy-expand-debug${params.toString() ? `?${params.toString()}` : ''}`;
  }, [quickFilters]);

  const debugTooltip = useMemo(() => {
    if (quickFilters.length === 0) return 'Debug expansion';
    const parts = quickFilters.map((f) => `${f.dimension.replace('_', ' ')}: ${f.name}`);
    return `Debug expansion for: ${parts.join(' • ')}`;
  }, [quickFilters]);

  const handleDatasetChange = (newDatasetIds: string[]) => {
    setDatasetIds(newDatasetIds);
    const params = new URLSearchParams(searchParams);

    if (newDatasetIds.length > 0) {
      params.set('datasetIds', newDatasetIds.join(','));
    } else {
      params.delete('datasetIds');
    }

    setSearchParams(params, { replace: true });
    // Don't call fetchSignals here - let useEffect handle it when datasetIds changes
  };

  const clearAllFilters = () => {
    // Clear all state
    setQuickFilters([]);
    setDatasetIds([]);

    // Clear all URL parameters in one atomic operation
    const params = new URLSearchParams(searchParams);
    params.delete('taxonomyCategoryKeys');
    params.delete('datasetIds');
    setSearchParams(params, { replace: true });

    // Fetch fresh data
    fetchSignals(1);
  };

  return {
    // data
    signals,
    loading,
    error,
    detailCache,
    expanded,
    threadOpen,
    selected,

    // pagination
    pagination,

    // flags
    deleting,
    progressTick,
    justUpdated,

    // search + filters
    searchTerm,
    setSearchTerm,
    quickFilters,
    debugExpandUrl,
    debugTooltip,
    datasetIds,
    handleDatasetChange,

    // actions
    fetchSignals,
    handleReclassifySignal,
    handleBulkReclassify,
    handleLabelTap,
    toggleExpand,
    toggleThread,
    ensureDetail,
    toggleSelect,
    toggleSelectAll,
    removeQuickFilter,
    clearQuickFilters,
    clearAllFilters,
    deleteSelected,
    allVisibleSelected,
    handleSort,
    sortBy,
    sortOrder,
  } as const;
}
